import {
    MessageCircle,
    Phone,
    Instagram,
    MapPin,
    Clock,
    ArrowRight,
} from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import {
    whatsappLink,
    PHONE_DISPLAY,
    INSTAGRAM_HANDLE,
    INSTAGRAM_URL,
    ADDRESS,
    BUSINESS_HOURS,
} from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isOpenNow = () => {
    const now = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
    const day = now.getDay();
    const hour = now.getHours();
    return day >= 1 && day <= 6 && hour >= 8 && hour < 20;
};

const CONTACT_ITEMS = [
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: PHONE_DISPLAY,
        href: whatsappLink(),
        testid: "contact-whatsapp-link",
    },
    {
        icon: Phone,
        label: "Telefone",
        value: PHONE_DISPLAY,
        href: `tel:+55${PHONE_DISPLAY.replace(/\D/g, "")}`,
        testid: "contact-phone-link",
    },
    {
        icon: Instagram,
        label: "Instagram",
        value: INSTAGRAM_HANDLE,
        href: INSTAGRAM_URL,
        testid: "contact-instagram-link",
    },
    {
        icon: MapPin,
        label: "Região de atendimento",
        value: ADDRESS,
        testid: "contact-address",
    },
    {
        icon: Clock,
        label: "Horário de atendimento",
        value: BUSINESS_HOURS,
        testid: "contact-hours",
    },
];

export const FinalCta = () => (
    <section
        data-testid="final-cta-section"
        className="relative bg-brand noise-bg py-24 lg:py-32 overflow-hidden"
    >
        <div
            className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-navy/15 blur-[120px] pointer-events-none"
            aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
            <Reveal>
                <h2 className="font-display font-extrabold text-white tracking-tighter text-4xl sm:text-6xl leading-[1.05]">
                    Seu ambiente merece um cuidado especial.
                </h2>
                <p className="mt-6 text-white/85 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                    Entre em contato com a Brandão Morais Higienização e solicite seu orçamento.
                </p>
                <a
                    data-testid="final-cta-whatsapp-button"
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-10 inline-flex items-center gap-3 bg-navy hover:bg-navy-900 text-white font-bold px-9 py-4 rounded-full transition-colors duration-300 text-base"
                >
                    <MessageCircle className="w-5 h-5" />
                    Solicitar orçamento pelo WhatsApp
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </a>
            </Reveal>
        </div>
    </section>
);

export const Contact = () => {
    const open = isOpenNow();

    return (
        <section id="contato" data-testid="contact-section" className="bg-white py-24 lg:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
                <div>
                    <Reveal>
                        <SectionLabel>Contato</SectionLabel>
                        <h2 className="mt-5 font-display font-bold text-navy tracking-tight text-4xl sm:text-5xl leading-[1.1]">
                            Fale com a <span className="text-brand">gente</span>
                        </h2>
                        <p className="mt-6 text-navy/60 text-base leading-relaxed max-w-md">
                            Estamos prontos para atender você. Escolha o canal de sua preferência
                            e fale direto com a gente pelo WhatsApp.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 bg-navy noise-bg rounded-3xl px-8 py-6 w-fit">
                            <img
                                data-testid="contact-logo-full"
                                src="/logo-full.png"
                                alt="Brandão Morais Higienização — WhatsApp (11) 99306-2119 — Instagram @brandaomoraishigienizacao"
                                className="h-24 w-auto"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <span
                            data-testid="open-now-badge"
                            className={`mt-5 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-xs font-bold tracking-wide ${
                                open
                                    ? "bg-green-500/10 text-green-600 border border-green-500/30"
                                    : "bg-navy/5 text-navy/55 border border-navy/15"
                            }`}
                        >
                            <span
                                className={`w-2 h-2 rounded-full ${
                                    open ? "bg-green-500 animate-pulse" : "bg-navy/40"
                                }`}
                            />
                            {open
                                ? "Aberto agora — atendemos hoje até às 20h"
                                : "Fechado no momento — chame no WhatsApp e retornamos em seguida"}
                        </span>
                    </Reveal>
                    <div className="mt-8 space-y-4">
                        {CONTACT_ITEMS.map((item, i) => {
                            const Icon = item.icon;
                            const inner = (
                                <>
                                    <span className="w-12 h-12 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-brand">
                                        <Icon className="w-5 h-5 text-brand transition-colors duration-300 group-hover:text-white" strokeWidth={1.8} />
                                    </span>
                                    <div>
                                        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-navy/45">
                                            {item.label}
                                        </p>
                                        <p className="font-bold text-navy text-base">{item.value}</p>
                                    </div>
                                </>
                            );
                            return (
                                <Reveal key={item.label} delay={i * 0.06}>
                                    {item.href ? (
                                        <a
                                            data-testid={item.testid}
                                            href={item.href}
                                            target={item.href.startsWith("http") ? "_blank" : undefined}
                                            rel="noopener noreferrer"
                                            className="group flex items-center gap-5 border-2 border-navy/10 hover:border-brand rounded-2xl px-5 py-4 transition-colors duration-300"
                                        >
                                            {inner}
                                        </a>
                                    ) : (
                                        <div
                                            data-testid={item.testid}
                                            className="group flex items-center gap-5 border-2 border-dashed border-navy/15 rounded-2xl px-5 py-4"
                                        >
                                            {inner}
                                        </div>
                                    )}
                                </Reveal>
                            );
                        })}
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <div
                        data-testid="contact-whatsapp-card"
                        className="bg-navy noise-bg rounded-3xl p-8 sm:p-12 h-full flex flex-col justify-center"
                    >
                        <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight">
                            Seu orçamento está a{" "}
                            <span className="text-brand">um toque</span>
                        </h3>
                        <p className="mt-5 text-white/65 text-base leading-relaxed">
                            Chame no WhatsApp, conte o que você precisa higienizar e receba seu
                            orçamento de forma rápida e sem compromisso.
                        </p>
                        <a
                            data-testid="contact-whatsapp-card-button"
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group mt-9 inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-hover text-white font-bold px-9 py-4 rounded-full transition-colors duration-300 w-full sm:w-fit"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Chamar no WhatsApp
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </a>
                        <p className="mt-6 text-xs text-white/40">
                            {BUSINESS_HOURS} — retornamos assim que possível.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
